import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { LifeBuoy, BookOpen, MessageSquarePlus, Inbox, PlayCircle, Shield, ListChecks, Trash2 } from "lucide-react";

/**
 * HelpDropdown — top-right utility cluster Help menu.
 *
 * Phase B3 replaces the previous static HelpCircle icon with a 5-item menu
 * (6 for super_admin) anchored on a `LifeBuoy` lucide icon. Items are
 * stable test-ided so e1_tester can drive each route directly without
 * relying on visible copy.
 *
 * Phase B4 wires the "Restart onboarding tour" item — broadcasts a
 * `entrybook:restart-tour` window event consumed by OnboardingHost — and
 * adds a "Show go-live checklist" item that surfaces only when the
 * widget has been dismissed (show_checklist_widget=false).
 */
export default function HelpDropdown() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === "super_admin";
  const [widgetHidden, setWidgetHidden] = useState(false);
  // Phase B5 — count of onboarding-sample docs so we can surface the
  // "Clear onboarding sample data" shortcut only when it's relevant.
  const [sampleCount, setSampleCount] = useState(0);

  // Poll /onboarding/me lightly to know whether to surface the "Show
  // checklist" item. Cheap call, only fires when the dropdown opens.
  const refreshWidgetFlag = async () => {
    if (!user || user.role === "super_admin") return;
    try {
      const { data } = await api.get("/onboarding/me");
      setWidgetHidden(data?.tour?.show_checklist_widget === false);
    } catch { /* silent */ }
  };

  // Phase B5 — light preview on dropdown open to populate sample count.
  // Same RBAC as the page itself; the request can 4xx silently for
  // non-admins without polluting the bell.
  const refreshSampleCount = async () => {
    if (!user || user.role !== "org_admin" || !user.org_id) return;
    try {
      const { data } = await api.post("/data-cleanup/preview", {
        org_id: user.org_id,
        scope: "by_source_tag",
        source_tag: "onboarding_sample",
      });
      setSampleCount(data?.total || 0);
    } catch { setSampleCount(0); }
  };

  useEffect(() => { refreshWidgetFlag(); refreshSampleCount(); /* on mount */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const go = (path) => () => navigate(path);

  const restartTour = async () => {
    if (!user || user.role !== "org_admin") {
      // For non-org_admin we silently no-op rather than erroring — the
      // menu item is hidden for those roles anyway.
      return;
    }
    try {
      // OnboardingHost owns the reset call + tour kick-off. Just signal it.
      window.dispatchEvent(new CustomEvent("entrybook:restart-tour"));
      toast.success("Tour restarted.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const showChecklist = () => {
    // OnboardingHost owns the PATCH + toast; we just signal it.
    window.dispatchEvent(new CustomEvent("entrybook:show-checklist"));
    setWidgetHidden(false);
  };

  // Suppress the tour-only items for non-org_admin roles — they don't
  // have an onboarding tour to restart (host / receptionist have no
  // walkthrough yet; super_admin uses a separate workspace).
  const showTourItems = user?.role === "org_admin";

  return (
    <DropdownMenu onOpenChange={(open) => { if (open) { refreshWidgetFlag(); refreshSampleCount(); } }}>
      <DropdownMenuTrigger asChild>
        <button
          className="w-9 h-9 rounded-full hover:bg-navy-50 dark:hover:bg-navy-900 flex items-center justify-center text-navy-600 dark:text-navy-300"
          aria-label="Help"
          data-testid="help-dropdown-trigger"
        >
          <LifeBuoy className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-60"
        data-testid="help-dropdown-content"
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-navy-500">
          Help &amp; support
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={go("/help/kb")} data-testid="help-menu-kb">
          <BookOpen className="w-4 h-4 mr-2" /> Knowledge base
        </DropdownMenuItem>
        <DropdownMenuItem onClick={go("/help/tickets/new")} data-testid="help-menu-new-ticket">
          <MessageSquarePlus className="w-4 h-4 mr-2" /> Submit a ticket
        </DropdownMenuItem>
        <DropdownMenuItem onClick={go("/help/tickets")} data-testid="help-menu-tickets">
          <Inbox className="w-4 h-4 mr-2" /> My tickets
        </DropdownMenuItem>
        {showTourItems && (
          <DropdownMenuItem onClick={restartTour} data-testid="help-menu-restart-tour">
            <PlayCircle className="w-4 h-4 mr-2" /> Restart onboarding tour
          </DropdownMenuItem>
        )}
        {showTourItems && widgetHidden && (
          <DropdownMenuItem onClick={showChecklist} data-testid="help-menu-show-checklist">
            <ListChecks className="w-4 h-4 mr-2" /> Show go-live checklist
          </DropdownMenuItem>
        )}
        {showTourItems && sampleCount > 0 && (
          <DropdownMenuItem
            onClick={() => navigate("/settings/data?focus=sample")}
            data-testid="help-menu-clear-sample"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear onboarding sample data
            <span className="ml-auto text-[10px] text-navy-400">{sampleCount}</span>
          </DropdownMenuItem>
        )}
        {isSuper && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-navy-500">
              Super admin
            </DropdownMenuLabel>
            <DropdownMenuItem onClick={go("/super-admin/tickets")} data-testid="help-menu-super-tickets">
              <Shield className="w-4 h-4 mr-2" /> Open ticket console
            </DropdownMenuItem>
            <DropdownMenuItem onClick={go("/super-admin/kb")} data-testid="help-menu-super-kb">
              <BookOpen className="w-4 h-4 mr-2" /> Manage knowledge base
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
